<script setup lang="ts">
import { ref, reactive, computed, useAttrs } from 'vue';
import type { UiImgProps } from './types';

const attrs = useAttrs();
const passedClass = attrs.class as string | undefined;
const { class: _, ...restAttrs } = attrs;

const { $isWebpSupport } = useNuxtApp();
const {
  src,
  alt,
  width,
  tag = 'div',
  lazy = true,
  decoding = 'async',
} = defineProps<UiImgProps>();

const parseFormat = src.split('.').pop()?.split(':')[0];
const srcFormat = parseFormat && ['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif'].includes(parseFormat)
  ? parseFormat
  : 'png';

const imgFormat = $isWebpSupport ? 'webp' : srcFormat;

const imgBlock = ref<HTMLElement | null>(null);

const imgBlockSize = useElementSize(imgBlock);
const blockSize = reactive(imgBlockSize);

const blockWidth = computed(() => Math.ceil(blockSize.width));
const blockHeight = computed(() => Math.ceil(blockSize.height));

const { pixelRatio } = reactive(useDevicePixelRatio());
const imgSize = computed(() => Math.ceil(blockSize.width * pixelRatio));

const runtimeConfig = useRuntimeConfig();
const imgApiUrl = runtimeConfig.public.imgApiUrl;
const imgQuality = 100;

const imgSrcClient = computed(
  () => `${imgApiUrl}?src=${src}&width=${imgSize.value || width}&format=${imgFormat}&quality=${imgQuality}`,
);

const imgSrcServer = computed(
  () => `${imgApiUrl}?src=${src}&width=${width}&format=${imgFormat}&quality=${imgQuality}`,
);

const isImageLoaded = ref(false);
const onLoad = () => {
  isImageLoaded.value = true;
};
</script>

<template>
  <component
    :is="tag"
    ref="imgBlock"
    role="img"
    class="block w-full overflow-hidden ui-img"
    :class="passedClass"
    v-bind="restAttrs"
  >
    <ClientOnly>
      <img
        data-allow-mismatch
        :src="imgSrcClient"
        :alt="alt"
        :width="blockWidth"
        :height="blockHeight"
        :loading="lazy ? 'lazy' : 'eager'"
        :decoding="decoding"
        :class="[
          'transition-opacity duration-300 ease-in-out',
          isImageLoaded ? 'opacity-100' : 'opacity-0',
        ]"
        @load="onLoad"
      >
      <template #fallback>
        <img
          key="ssr"
          data-allow-mismatch
          :src="imgSrcServer"
          :alt="alt"
          :width="blockWidth"
          :height="blockHeight"
          :loading="lazy ? 'lazy' : 'eager'"
          :decoding="decoding"
          class="opacity-100"
        >
      </template>
    </ClientOnly>
  </component>
</template>
